import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Table,
  Badge,
} from "reactstrap";
import { injectIntl } from "react-intl";
import axios from "axios";
import { NotificationManager } from "../../../../components/common/react-notifications";
import { END_POINT } from "../../../../constants/defaultValues";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const StockDisposalForm = ({
  intl,
  isOpen,
  toggle,
  onSuccess,
  stocks = [],
  providers = [],
}) => {
  const [disposalDate, setDisposalDate] = useState(moment());
  const [disposalType, setDisposalType] = useState("expired");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const disposalTypes = [
    { value: "expired", label: "Hết hạn sử dụng" },
    { value: "damaged", label: "Hư hỏng" },
    { value: "other", label: "Lý do khác" },
  ];

  const resetForm = () => {
    setDisposalDate(moment());
    setDisposalType("expired");
    setNotes("");
    setItems([]);
  };

  const handleClose = () => {
    resetForm();
    toggle();
  };

  const addItem = (stock) => {
    if (items.find((i) => i.stock_id === stock.id)) {
      NotificationManager.warning("Nguyên liệu đã được thêm", "Cảnh báo");
      return;
    }

    // Tự động tìm provider
    let matchedProviderId = "";
    for (const provider of providers) {
      const providerItems = provider.company_provider_item || [];
      const found = providerItems.find(
        (item) => String(item.stock_id) === String(stock.id)
      );
      if (found) {
        matchedProviderId = provider.id;
        break;
      }
    }
    // Nếu không tìm thấy, dùng provider đầu tiên nếu chỉ có 1
    if (!matchedProviderId && providers.length === 1) {
      matchedProviderId = providers[0].id;
    }

    setItems([
      ...items,
      {
        stock_id: stock.id,
        stock_name: stock.name,
        stock_unit: stock.unit,
        quantity: 1,
        unit_price: stock.avarage_price || 0,
        current_count: stock.count,
        reason: "",
        provider_id: matchedProviderId,
        is_refundable: false,
      },
    ]);
  };

  const removeItem = (stockId) => {
    setItems(items.filter((i) => i.stock_id !== stockId));
  };

  const updateItem = (stockId, field, value) => {
    setItems(
      items.map((i) => (i.stock_id === stockId ? { ...i, [field]: value } : i))
    );
  };

  const getTotalValue = () => {
    return items.reduce(
      (sum, item) => sum + item.quantity * item.unit_price,
      0
    );
  };

  const handleSubmit = async () => {
    if (items.length === 0) {
      NotificationManager.warning(
        "Vui lòng thêm nguyên liệu cần tiêu hủy",
        "Cảnh báo"
      );
      return;
    }

    for (const item of items) {
      if (item.quantity <= 0) {
        NotificationManager.warning("Số lượng phải lớn hơn 0", "Cảnh báo");
        return;
      }
      if (item.quantity > item.current_count) {
        NotificationManager.warning(
          `Số lượng tiêu hủy của "${item.stock_name}" vượt quá tồn kho`,
          "Cảnh báo"
        );
        return;
      }
    }

    setLoading(true);
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.post(
        `${END_POINT}/api/stock-disposal`,
        {
          disposal_date: moment(disposalDate).format("YYYY-MM-DD"),
          disposal_type: disposalType,
          notes,
          items: items.map((i) => ({
            stock_id: i.stock_id,
            quantity: i.quantity,
            unit_price: i.unit_price,
            reason: i.reason,
            provider_id: i.provider_id || null,
            is_refundable: i.is_refundable,
          })),
        },
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );

      if (response.data.status === 200) {
        NotificationManager.success(response.data.responseText, "Thành công");
        onSuccess && onSuccess();
        handleClose();
      } else {
        NotificationManager.error(response.data.responseText, "Lỗi");
      }
    } catch (error) {
      NotificationManager.error("Có lỗi xảy ra", "Lỗi");
    }
    setLoading(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("vi-VN").format(value || 0);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      style={{ maxWidth: "90vw", width: "90vw" }}
    >
      <ModalHeader toggle={handleClose}>Tiêu Hủy Nguyên Liệu</ModalHeader>
      <ModalBody style={{ maxHeight: "90vh", overflowY: "auto" }}>
        <div className="row">
          <div className="col-md-6">
            <FormGroup>
              <Label>Ngày tiêu hủy</Label>
              <DatePicker
                selected={disposalDate ? moment(disposalDate) : null}
                onChange={(date) => setDisposalDate(date)}
                dateFormat="DD-MM-YYYY"
                className="form-control"
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label>Loại tiêu hủy</Label>
              <Input
                type="select"
                value={disposalType}
                onChange={(e) => setDisposalType(e.target.value)}
              >
                {disposalTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </div>
        </div>

        {/* Chọn nguyên liệu */}
        <FormGroup>
          <Label>Chọn nguyên liệu cần tiêu hủy</Label>
          <Input
            type="select"
            onChange={(e) => {
              const stock = stocks.find(
                (s) => s.id === parseInt(e.target.value)
              );
              if (stock) addItem(stock);
              e.target.value = "";
            }}
          >
            <option value="">-- Chọn nguyên liệu --</option>
            {stocks
              .filter((s) => !items.find((i) => i.stock_id === s.id))
              .map((stock) => (
                <option key={stock.id} value={stock.id}>
                  {stock.name} (Tồn: {stock.count} {stock.unit})
                </option>
              ))}
          </Input>
        </FormGroup>

        {/* Danh sách nguyên liệu */}
        {items.length > 0 && (
          <Table size="sm" responsive>
            <thead>
              <tr>
                <th style={{ verticalAlign: "middle" }}>Nguyên liệu</th>
                <th className="text-center" style={{ verticalAlign: "middle" }}>
                  Tồn kho
                </th>
                <th
                  className="text-center"
                  style={{ width: "100px", verticalAlign: "middle" }}
                >
                  Số lượng
                </th>
                <th
                  className="text-center"
                  style={{ width: "240px", verticalAlign: "middle" }}
                >
                  Nhà cung cấp
                </th>
                <th
                  className="text-center"
                  style={{ width: "220px", verticalAlign: "middle" }}
                >
                  Hoàn tiền
                </th>
                <th className="text-right" style={{ verticalAlign: "middle" }}>
                  Giá trị
                </th>
                <th style={{ width: "250px", verticalAlign: "middle" }}>
                  Lý do cụ thể
                </th>
                <th style={{ width: "50px", verticalAlign: "middle" }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.stock_id}>
                  <td style={{ verticalAlign: "middle" }}>{item.stock_name}</td>
                  <td
                    className="text-center"
                    style={{ verticalAlign: "middle" }}
                  >
                    {item.current_count} {item.stock_unit}
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    <Input
                      type="number"
                      bsSize="sm"
                      value={item.quantity}
                      onChange={(e) =>
                        updateItem(
                          item.stock_id,
                          "quantity",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      min="0"
                      max={item.current_count}
                    />
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    <Input
                      type="select"
                      bsSize="sm"
                      value={item.provider_id || ""}
                      onChange={(e) =>
                        updateItem(item.stock_id, "provider_id", e.target.value)
                      }
                    >
                      <option value="">-- Chọn --</option>
                      {providers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </Input>
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    <Input
                      type="select"
                      bsSize="sm"
                      value={item.is_refundable ? "true" : "false"}
                      onChange={(e) =>
                        updateItem(
                          item.stock_id,
                          "is_refundable",
                          e.target.value === "true"
                        )
                      }
                    >
                      <option value="false">Không được hoàn tiền</option>
                      <option value="true">Được hoàn tiền</option>
                    </Input>
                  </td>
                  <td
                    className="text-right"
                    style={{ verticalAlign: "middle" }}
                  >
                    {formatCurrency(item.quantity * item.unit_price)} đ
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    <Input
                      type="text"
                      bsSize="sm"
                      value={item.reason}
                      onChange={(e) =>
                        updateItem(item.stock_id, "reason", e.target.value)
                      }
                      placeholder="Lý do..."
                    />
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    <Button
                      color="link"
                      size="sm"
                      className="text-danger"
                      onClick={() => removeItem(item.stock_id)}
                    >
                      <i className="simple-icon-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="5" className="text-right">
                  <strong>Tổng giá trị tiêu hủy:</strong>
                </td>
                <td className="text-right text-danger">
                  <strong>{formatCurrency(getTotalValue())} đ</strong>
                </td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </Table>
        )}

        <FormGroup>
          <Label>Ghi chú</Label>
          <Input
            type="textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
        </FormGroup>

        <div className="alert alert-info">
          <i className="simple-icon-info mr-2"></i>
          <small>
            Nếu bạn là Admin, phiếu tiêu hủy sẽ được thực hiện ngay. Nếu không,
            phiếu sẽ chờ Admin duyệt.
          </small>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={loading}>
          Hủy
        </Button>
        <Button color="danger" onClick={handleSubmit} disabled={loading}>
          {loading ? "Đang xử lý..." : "Tiêu hủy"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default injectIntl(StockDisposalForm);
